import { ApolloLink, Observable } from '@apollo/client';

import type { APITrace } from './types';
import type { SuccessfulGqlMutationCallback } from './successfulMutationTracking';
import { notifySuccessfulGqlMutations } from './successfulMutationTracking';

export type OperationsListLinkArgs = {
  operationPrefixNameForAddingToRawLogOutputs: string;
  onOperationsChange?: (operations: string[]) => void;
  onOperationStart?: (operation: string) => void;
  onOperationFinish?: (operation: string, apiTraces: Array<APITrace>) => void;
  onSuccessfulMutation?: SuccessfulGqlMutationCallback;
};

export const OperationsListLink = (args: OperationsListLinkArgs) => {
  return new ApolloLink((operation, forward) => {
    if (args.onOperationStart) {
      args.onOperationStart(operation.operationName);
    }

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          notifySuccessfulGqlMutations({
            operation,
            response: result,
            onSuccessfulMutation: args.onSuccessfulMutation,
          });

          if (args.onOperationFinish) {
            let apiTraces: Array<APITrace> = [];

            if (result.extensions?.api_traces?.length) {
              apiTraces = result.extensions.api_traces.map(
                // Don't want to incur perf penalty of parsing here, just believe the type and adjust as needed.
                (trace: { duration: string; method: string; url: string }) => {
                  return {
                    durationMS: parseInt(trace.duration),
                    method: trace.method,
                    url: trace.url,
                  };
                }
              );
            }
            args.onOperationFinish(operation.operationName, apiTraces);
          }

          observer.next(result);
        },
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  });
};
